import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { errText } from "./AdjustInventoryModal";
import { downloadCsv } from "./InventoryHistoryTab";
import { fmtDate } from "./InventoryShared";

const STEPS = ["Upload", "Map columns", "Preview & validate", "Confirm", "Done"];
const ACTIONS = [["UPDATE_BOTH", "Update both"], ["UPDATE_PRODUCT", "Update product"], ["UPDATE_INVENTORY", "Update inventory"], ["SKIP", "Skip"]];

export const InventoryImportExportTab = () => {
  const [mode, setMode] = useState("import");
  return (
    <div data-testid="import-export-tab">
      <div className="flex gap-1 mb-4">
        {[["import", "Import inventory"], ["export", "Export inventory"], ["price", "Bulk price update"], ["history", "Import history"]].map(([k, l]) => (
          <button key={k} data-testid={`ie-mode-${k}`} onClick={() => setMode(k)} className={`px-4 h-9 rounded-full text-sm font-bold ${mode === k ? "bg-brand-red text-white" : "bg-white border border-slate-200"}`}>{l}</button>
        ))}
      </div>
      {mode === "import" && <ImportWizard />}
      {mode === "export" && <ExportPanel />}
      {mode === "price" && <PriceUpdate />}
      {mode === "history" && <ImportHistory />}
    </div>
  );
};

const Stepper = ({ step }) => (
  <div className="flex flex-wrap gap-2 mb-5" data-testid="import-stepper">
    {STEPS.map((s, i) => (
      <div key={s} className={`flex items-center gap-2 text-xs font-bold ${i === step ? "text-brand-red" : i < step ? "text-emerald-600" : "text-slate-400"}`}>
        <span className={`h-6 w-6 rounded-full grid place-items-center text-[11px] ${i === step ? "bg-brand-red text-white" : i < step ? "bg-emerald-100" : "bg-slate-100"}`}>{i + 1}</span>{s}{i < STEPS.length - 1 && <span className="text-slate-300">›</span>}
      </div>
    ))}
  </div>
);

const ImportWizard = () => {
  const [step, setStep] = useState(0);
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/inventory/import/upload", fd);
      setUpload(r.data); setMapping(r.data.mapping); setStep(1);
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inventory/import/preview", { upload_id: upload.upload_id, mapping });
      setPreview(r.data); setRows(r.data.rows); setStep(2);
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inventory/import/commit", { upload_id: upload.upload_id, rows, confirm: true });
      setResult(r.data); setStep(4); toast.success("Import complete");
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const setAction = (rowNo, action) => setRows((rs) => rs.map((r) => (r.row === rowNo ? { ...r, action } : r)));
  const setAllExisting = (action) => setRows((rs) => rs.map((r) => (r.state === "EXISTING" ? { ...r, action } : r)));
  const counts = {
    create: rows.filter((r) => r.action === "CREATE").length,
    update: rows.filter((r) => r.state === "EXISTING" && r.action !== "SKIP").length,
    skip: rows.filter((r) => r.state === "EXISTING" && r.action === "SKIP").length,
    errors: rows.filter((r) => r.state === "ERROR").length,
    warnings: rows.filter((r) => r.warnings?.length).length,
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="import-wizard">
      <Stepper step={step} />
      {step === 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <label className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-brand-navy block" data-testid="import-dropzone">
            <Upload className="h-8 w-8 mx-auto text-slate-400" />
            <div className="font-bold mt-2">{busy ? "Uploading..." : "Choose a CSV file"}</div>
            <div className="text-xs text-slate-500">Max 5MB · header row required</div>
            <input type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          <div className="bg-slate-50 rounded-2xl p-5">
            <FileSpreadsheet className="h-6 w-6 text-brand-red" />
            <div className="font-heading text-lg text-brand-navy mt-2">Start from the template</div>
            <p className="text-sm text-slate-600 mt-1">Columns: SKU, Barcode, Product Name, Category, Brand, Condition, Inventory Type, Cost, Price, Quantity, Reorder Level, Location, Aisle, Rack, Shelf, Bin, Description, Status.</p>
            <button data-testid="download-template-btn" onClick={() => downloadCsv("/inventory/template", {}, "inventory-import-template.csv")}
              className="mt-3 inline-flex items-center gap-1 bg-brand-navy text-white rounded-full h-10 px-4 font-bold text-sm"><Download className="h-4 w-4" /> Download CSV template</button>
          </div>
        </div>
      )}
      {step === 1 && upload && (
        <div>
          <div className="text-sm text-slate-600 mb-3"><b>{upload.file_name}</b> · {upload.row_count} rows. Match each field to a column in your file (SKU is required).</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="mapping-grid">
            {upload.template_fields.map((f) => (
              <label key={f} className="flex items-center gap-2 text-sm">
                <span className={`w-32 font-bold ${f === "SKU" ? "text-brand-red" : "text-slate-600"}`}>{f}</span>
                <select data-testid={`map-${f.replace(/\s/g, "-").toLowerCase()}`} value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value || null })} className="flex-1 h-9 rounded-lg border border-slate-200 px-2 bg-white">
                  <option value="">— not mapped —</option>{upload.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button data-testid="mapping-next-btn" disabled={!mapping.SKU || busy} onClick={runPreview} className="bg-brand-navy text-white rounded-full h-11 px-6 font-bold disabled:opacity-50">Preview &amp; validate</button>
            <button onClick={() => setStep(0)} className="h-11 px-4 rounded-full border border-slate-200 font-bold">Back</button>
          </div>
        </div>
      )}
      {(step === 2 || step === 3) && preview && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="import-summary">
            <Sum label="To create" v={counts.create} c="text-emerald-600" /><Sum label="To update" v={counts.update} c="text-brand-navy" />
            <Sum label="To skip" v={counts.skip} c="text-slate-500" /><Sum label="Warnings" v={counts.warnings} c="text-yellow-600" /><Sum label="Errors" v={counts.errors} c="text-brand-red" />
          </div>
          {counts.errors > 0 && <div className="text-sm text-brand-red font-bold mb-2 flex items-center gap-1"><XCircle className="h-4 w-4" /> Rows with errors will not be imported.</div>}
          {rows.some((r) => r.state === "EXISTING") && (
            <div className="flex flex-wrap items-center gap-2 text-sm mb-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2" data-testid="existing-actions">
              <AlertTriangle className="h-4 w-4 text-yellow-600" /> Existing SKUs found. Apply to all:
              {ACTIONS.map(([a, l]) => <button key={a} data-testid={`all-existing-${a}`} onClick={() => setAllExisting(a)} className="h-8 px-3 rounded-full bg-white border border-slate-200 font-bold text-xs">{l}</button>)}
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-[420px]">
            <table className="w-full text-xs" data-testid="preview-table">
              <thead className="bg-slate-50 text-left uppercase tracking-wider text-slate-500 sticky top-0">
                <tr><th className="p-2">Row</th><th className="p-2">State</th><th className="p-2">SKU</th><th className="p-2">Product</th><th className="p-2">Category</th><th className="p-2">Type</th><th className="p-2">Qty</th><th className="p-2">Price</th><th className="p-2">Location</th><th className="p-2">Issues</th><th className="p-2">Action</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.row} className={`border-t border-slate-100 ${r.state === "ERROR" ? "bg-red-50/50" : r.state === "EXISTING" ? "bg-yellow-50/40" : ""}`} data-testid={`preview-row-${r.row}`}>
                    <td className="p-2 text-slate-500">{r.row}</td>
                    <td className="p-2"><StateBadge s={r.state} /></td>
                    <td className="p-2 font-bold">{r.sku || "—"}</td><td className="p-2">{r.product_name}</td><td className="p-2">{r.category_name || "—"}</td>
                    <td className="p-2">{r.inventory_type}</td><td className="p-2">{r.quantity}</td><td className="p-2">{r.price != null ? money(r.price) : "—"}</td><td className="p-2">{r.location_name}</td>
                    <td className="p-2">
                      {r.errors.map((e) => <div key={e} className="text-brand-red">✕ {e}</div>)}
                      {r.warnings.map((w) => <div key={w} className="text-yellow-700">⚠ {w}</div>)}
                    </td>
                    <td className="p-2">
                      {r.state === "EXISTING" ? (
                        <select value={r.action} onChange={(e) => setAction(r.row, e.target.value)} data-testid={`action-row-${r.row}`} className="h-8 rounded border border-slate-200 bg-white">
                          {ACTIONS.map(([a, l]) => <option key={a} value={a}>{l}</option>)}
                        </select>
                      ) : r.state === "VALID" ? <span className="text-emerald-600 font-bold">Create</span> : <span className="text-slate-400">Blocked</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {step === 2 ? (
            <div className="flex gap-2 mt-4">
              <button data-testid="preview-continue-btn" disabled={counts.create + counts.update === 0} onClick={() => setStep(3)} className="bg-brand-navy text-white rounded-full h-11 px-6 font-bold disabled:opacity-50">Continue to confirm</button>
              <button onClick={() => setStep(1)} className="h-11 px-4 rounded-full border border-slate-200 font-bold">Back to mapping</button>
            </div>
          ) : (
            <div className="mt-4 bg-slate-50 rounded-xl p-4" data-testid="confirm-panel">
              <div className="font-heading text-lg text-brand-navy">Confirm import</div>
              <p className="text-sm text-slate-600 mt-1">This will create <b>{counts.create}</b> products, update <b>{counts.update}</b> existing SKUs, skip <b>{counts.skip}</b> and block <b>{counts.errors}</b> rows with errors{counts.warnings > 0 && <> ({counts.warnings} warnings acknowledged)</>}. Every quantity change is logged as an IMPORT transaction.</p>
              <div className="flex gap-2 mt-3">
                <button data-testid="import-confirm-btn" disabled={busy} onClick={commit} className="bg-brand-red text-white rounded-full h-11 px-6 font-bold disabled:opacity-60">{busy ? "Importing..." : "Confirm & import"}</button>
                <button onClick={() => setStep(2)} className="h-11 px-4 rounded-full border border-slate-200 font-bold">Back</button>
              </div>
            </div>
          )}
        </div>
      )}
      {step === 4 && result && (
        <div data-testid="import-result">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          <div className="font-heading text-xl text-brand-navy mt-2">Import {result.status.toLowerCase()}</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
            <Sum label="Processed" v={result.records_processed} /><Sum label="Created" v={result.records_created} c="text-emerald-600" />
            <Sum label="Updated" v={result.records_updated} c="text-brand-navy" /><Sum label="Skipped" v={result.records_skipped} c="text-slate-500" /><Sum label="Failed" v={result.records_failed} c="text-brand-red" />
          </div>
          <div className="flex gap-2 mt-4">
            {result.records_failed > 0 && <button data-testid="download-errors-btn" onClick={() => downloadCsv(`/inventory/imports/${result.import_id}/errors.csv`, {}, "import-errors.csv")} className="inline-flex items-center gap-1 border border-brand-red text-brand-red rounded-full h-10 px-4 font-bold text-sm"><Download className="h-4 w-4" /> Download error report</button>}
            <button onClick={() => { setStep(0); setUpload(null); setPreview(null); setRows([]); setResult(null); }} className="bg-brand-navy text-white rounded-full h-10 px-4 font-bold text-sm" data-testid="import-again-btn">Import another file</button>
          </div>
        </div>
      )}
    </div>
  );
};

const StateBadge = ({ s }) => {
  const cls = { VALID: "bg-emerald-100 text-emerald-700", EXISTING: "bg-yellow-100 text-yellow-800", ERROR: "bg-red-100 text-brand-red" }[s];
  return <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${cls}`}>{s === "EXISTING" ? "Existing product" : s}</span>;
};
const Sum = ({ label, v, c = "text-brand-navy" }) => (
  <div className="bg-slate-50 rounded-xl p-3"><div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div><div className={`font-heading text-2xl ${c}`}>{v}</div></div>
);

const ExportPanel = () => {
  const [scope, setScope] = useState("all");
  const [cat, setCat] = useState("");
  const [loc, setLoc] = useState("");
  const [cats, setCats] = useState([]);
  const [locs, setLocs] = useState([]);
  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data)); api.get("/inventory/locations").then((r) => setLocs(r.data)); }, []);
  const go = () => downloadCsv("/inventory/export", { scope, ...(cat && { category_id: cat }), ...(loc && { location_id: loc }) }, `inventory-${scope}.csv`);
  const sel = "h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="export-panel">
      <div className="font-heading text-xl text-brand-navy">Export inventory to CSV</div>
      <p className="text-sm text-slate-600 mt-1">Includes SKU, barcode, product, category, brand, condition, type, location, aisle/rack/shelf/bin, all quantities, reorder level, cost, price, status and last updated.</p>
      <div className="grid sm:grid-cols-3 gap-2 mt-4">
        <select data-testid="export-scope" className={sel} value={scope} onChange={(e) => setScope(e.target.value)}>
          {[["all", "All inventory"], ["available", "Available"], ["low_stock", "Low stock"], ["out_of_stock", "Out of stock"], ["held", "Held"], ["reserved", "Reserved"], ["damaged", "Damaged"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="export-category" className={sel} value={cat} onChange={(e) => setCat(e.target.value)}><option value="">All categories</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <select data-testid="export-location" className={sel} value={loc} onChange={(e) => setLoc(e.target.value)}><option value="">All locations</option>{locs.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}</select>
      </div>
      <button data-testid="export-download-btn" onClick={go} className="mt-4 inline-flex items-center gap-1 bg-brand-navy text-white rounded-full h-11 px-6 font-bold"><Download className="h-4 w-4" /> Download CSV</button>
    </div>
  );
};

const PriceUpdate = () => {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { setPrev((await api.post("/inventory/price-update/preview", fd)).data); } catch (e) { toast.error(errText(e)); }
  };
  const commit = async () => {
    setBusy(true);
    try { const r = await api.post("/inventory/price-update/commit", { rows: prev.rows, confirm: true }); toast.success(`${r.data.applied} prices updated`); setPrev(null); }
    catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="price-update-panel">
      <div className="font-heading text-xl text-brand-navy">Bulk price update</div>
      <p className="text-sm text-slate-600 mt-1">Upload a CSV with <b>SKU</b> and <b>New Price</b> columns. You'll review old vs new prices before anything changes. Every change is recorded in price history.</p>
      {!prev ? (
        <label className="mt-4 inline-flex items-center gap-2 bg-brand-navy text-white rounded-full h-11 px-6 font-bold cursor-pointer">
          <Upload className="h-4 w-4" /> Choose CSV<input type="file" accept=".csv" className="hidden" data-testid="price-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
      ) : (
        <div className="mt-4">
          <div className="text-sm mb-2"><b>{prev.valid}</b> valid · <b className="text-brand-red">{prev.errors}</b> errors</div>
          <div className="overflow-x-auto border rounded-xl max-h-80">
            <table className="w-full text-xs" data-testid="price-preview-table">
              <thead className="bg-slate-50 text-left uppercase text-slate-500"><tr><th className="p-2">SKU</th><th className="p-2">Product</th><th className="p-2">Old</th><th className="p-2">New</th><th className="p-2">Difference</th><th className="p-2">Issue</th></tr></thead>
              <tbody>{prev.rows.map((r) => (
                <tr key={r.row} className={`border-t ${r.error ? "bg-red-50/50" : ""}`}>
                  <td className="p-2 font-bold">{r.sku}</td><td className="p-2">{r.product_name}</td><td className="p-2">{r.old_price != null ? money(r.old_price) : "—"}</td>
                  <td className="p-2">{r.new_price != null ? money(r.new_price) : "—"}</td>
                  <td className={`p-2 font-bold ${r.difference < 0 ? "text-emerald-600" : "text-brand-red"}`}>{r.difference != null ? (r.difference > 0 ? "+" : "") + money(r.difference) : "—"}</td>
                  <td className="p-2 text-brand-red">{r.error}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="price-confirm-btn" disabled={busy || prev.valid === 0} onClick={commit} className="bg-brand-red text-white rounded-full h-11 px-6 font-bold disabled:opacity-50">Confirm {prev.valid} price changes</button>
            <button onClick={() => setPrev(null)} className="h-11 px-4 rounded-full border border-slate-200 font-bold">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const ImportHistory = () => {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/inventory/imports").then((r) => setItems(r.data)); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="import-history">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-3">Date/time</th><th className="p-3">File</th><th className="p-3">User</th><th className="p-3">Processed</th><th className="p-3">Created</th><th className="p-3">Updated</th><th className="p-3">Skipped</th><th className="p-3">Failed</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {!items ? <tr><td colSpan={10} className="p-6 text-center text-slate-400">Loading...</td></tr> : items.length === 0 ? <tr><td colSpan={10} className="p-6 text-center text-slate-400">No imports yet.</td></tr> :
              items.map((i) => (
                <tr key={i.import_id} className="border-t border-slate-100" data-testid={`import-row-${i.import_id}`}>
                  <td className="p-3 whitespace-nowrap">{fmtDate(i.created_at)}</td><td className="p-3 font-bold">{i.file_name}</td><td className="p-3">{i.user_name}</td>
                  <td className="p-3">{i.records_processed}</td><td className="p-3 text-emerald-600">{i.records_created}</td><td className="p-3">{i.records_updated}</td><td className="p-3">{i.records_skipped}</td><td className="p-3 text-brand-red">{i.records_failed}</td>
                  <td className="p-3"><span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${i.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : i.status === "PARTIAL" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-brand-red"}`}>{i.status}</span></td>
                  <td className="p-3 whitespace-nowrap">
                    <button onClick={() => api.get(`/inventory/imports/${i.import_id}`).then((r) => setDetail(r.data))} className="text-brand-navy font-bold text-xs mr-2" data-testid={`import-view-${i.import_id}`}>View</button>
                    {i.records_failed > 0 && <button onClick={() => downloadCsv(`/inventory/imports/${i.import_id}/errors.csv`, {}, "import-errors.csv")} className="text-brand-red font-bold text-xs">Errors CSV</button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {detail && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="import-detail-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 mt-10">
            <div className="flex justify-between"><div className="font-heading text-xl text-brand-navy">{detail.file_name}</div><button onClick={() => setDetail(null)} className="font-bold text-slate-500">Close</button></div>
            <div className="text-xs text-slate-500 mb-3">{detail.import_id} · {fmtDate(detail.created_at)} · {detail.user_name}</div>
            {detail.errors?.length ? (
              <table className="w-full text-xs border rounded-xl"><thead className="bg-slate-50 text-left"><tr><th className="p-2">Row</th><th className="p-2">SKU</th><th className="p-2">Error</th><th className="p-2">Suggested fix</th></tr></thead>
                <tbody>{detail.errors.map((e, i) => <tr key={i} className="border-t"><td className="p-2">{e.row}</td><td className="p-2">{e.sku}</td><td className="p-2 text-brand-red">{e.error}</td><td className="p-2">{e.suggested_fix}</td></tr>)}</tbody></table>
            ) : <div className="text-sm text-emerald-600 font-bold">No errors in this import.</div>}
          </div>
        </div>
      )}
    </div>
  );
};
